/**
 * ScoreBadge Component
 *
 * A prominent badge showing the data quality score with color coding and label.
 * Colors are env-configurable via REACT_APP_SCORE_COLOR_* variables.
 *   Gray: no score available
 */
import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import { getScoreColorNullable } from "./scoreTheme";

interface IScoreBadgeProps {
  score: number | null | undefined;
  onClick?: () => void;
  size?: "small" | "medium";
  tooltipText?: string;
  showPrefix?: boolean;
  prefixLabel?: string;
}

const getScoreLabel = (score: number | null | undefined): string => {
  if (score === null || score === undefined) return "N/A";
  return `${(score * 100).toFixed(1)}%`;
};

const ScoreBadge: React.FC<IScoreBadgeProps> = ({
  score,
  onClick,
  size = "small",
  tooltipText,
  showPrefix = false,
  prefixLabel = "DQ Score:",
}) => {
  const color = getScoreColorNullable(score);
  const label = getScoreLabel(score);
  const hasScore = score !== null && score !== undefined;
  const isClickable = hasScore && Boolean(onClick);
  const tooltip = isClickable
    ? tooltipText || "Click to view score breakdown"
    : hasScore
      ? `Score: ${label}`
      : "Score not yet calculated";

  return (
    <Tooltip title={tooltip} describeChild={isClickable} arrow>
      <Box
        component={isClickable ? "button" : "span"}
        type={isClickable ? "button" : undefined}
        onClick={isClickable ? onClick : undefined}
        sx={{
          border: 0,
          margin: 0,
          font: "inherit",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          backgroundColor: color,
          color: "#fff",
          borderRadius: "20px",
          padding: size === "small" ? "4px 12px 4px 8px" : "6px 16px 6px 10px",
          cursor: isClickable ? "pointer" : "default",
          transition: "opacity 0.2s",
          "&:hover": isClickable ? { opacity: 0.85 } : {},
          "&:focus-visible": isClickable
            ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 }
            : {},
        }}
      >
        <AssessmentIcon sx={{ fontSize: size === "small" ? 16 : 20 }} />
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: size === "small" ? "0.8rem" : "0.95rem",
            lineHeight: 1,
            letterSpacing: "0.02em",
          }}
        >
          {showPrefix ? `${prefixLabel} ${label}` : label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default ScoreBadge;
